import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auditAPI, fraudAPI, gdprAPI, advancedAnalyticsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, ShieldWarning, ClipboardText, ChartLineUp, UserMinus,
  Funnel, Eye, XCircle, CheckCircle, Clock, Warning, CaretDown, LinkSimple, LockKey
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TABS = [
  { id: 'analytics', label: 'Analytics', icon: <ChartLineUp className="w-4 h-4" /> },
  { id: 'fraud', label: 'Fraud', icon: <ShieldWarning className="w-4 h-4" /> },
  { id: 'audit', label: 'Audit Log', icon: <ClipboardText className="w-4 h-4" /> },
  { id: 'chain', label: 'Audit Chain', icon: <LinkSimple className="w-4 h-4" /> },
  { id: 'gdpr', label: 'GDPR', icon: <UserMinus className="w-4 h-4" /> },
];

export default function AdminSecurityPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('analytics');

  if (user?.role !== 'admin') {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">Admin access required</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-20 bg-[#0A0A0A]/90 border-b border-[#222222] backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#999999] hover:text-[#22C55E] transition" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <span className="text-white font-bold">Security & Compliance</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex gap-1 mb-6 overflow-x-auto" data-testid="security-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                tab === t.id ? 'bg-[#22C55E] text-black' : 'bg-[#111111] text-[#999999] border border-[#333333] hover:border-[#555555]'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'fraud' && <FraudTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'chain' && <AuditChainTab />}
        {tab === 'gdpr' && <GDPRTab />}
      </div>
    </div>
  );
}


function AnalyticsTab() {
  const [kpis, setKPIs] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [cohorts, setCohorts] = useState(null);

  useEffect(() => {
    Promise.all([
      advancedAnalyticsAPI.getKPIs().then(r => setKPIs(r.data)),
      advancedAnalyticsAPI.getFunnel().then(r => setFunnel(r.data)),
      advancedAnalyticsAPI.getRevenueForecast().then(r => setForecast(r.data)),
      advancedAnalyticsAPI.getCohorts('monthly').then(r => setCohorts(r.data)),
    ]).catch((err) => console.error('Failed to load advanced analytics:', err));
  }, []);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Users', value: kpis.total_users },
            { label: 'Active (30d)', value: kpis.active_users_30d },
            { label: 'ARPU', value: `$${kpis.arpu}` },
            { label: 'Churn Rate', value: `${kpis.churn_rate}%` },
            { label: 'New This Week', value: kpis.new_users_week },
            { label: 'New This Month', value: kpis.new_users_month },
            { label: 'Total Revenue', value: `$${kpis.total_revenue}` },
            { label: 'Transactions', value: kpis.total_transactions },
          ].map((kpi) => (
            <Card key={kpi.label} className="bg-[#111111] border-[#222222]">
              <CardContent className="p-3">
                <p className="text-xs text-[#999999] uppercase tracking-wider">{kpi.label}</p>
                <p className="text-xl font-bold text-white mt-1">{kpi.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Funnel */}
      {funnel && (
        <Card className="bg-[#111111] border-[#222222]">
          <CardContent className="p-5">
            <h3 className="text-white font-bold mb-4">User Funnel</h3>
            <div className="space-y-2">
              {funnel.stages.map((stage) => {
                const maxCount = funnel.stages[0]?.count || 1;
                const pct = Math.round((stage.count / maxCount) * 100);
                return (
                  <div key={stage.name} className="flex items-center gap-3">
                    <span className="text-sm text-[#999999] w-36 shrink-0">{stage.name}</span>
                    <div className="flex-1 h-8 bg-[#0A0A0A] rounded-lg overflow-hidden relative">
                      <div className="h-full bg-[#22C55E]/20 rounded-lg transition-all" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-3 text-sm font-bold text-white">{stage.count} ({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Forecast */}
      {forecast && (
        <Card className="bg-[#111111] border-[#222222]">
          <CardContent className="p-5">
            <h3 className="text-white font-bold mb-4">Revenue Trend & Forecast</h3>
            <div className="flex gap-2 overflow-x-auto">
              {[...forecast.historical, ...forecast.forecast].map((m) => (
                <div key={m.month} className={`flex-1 min-w-[80px] text-center p-2 rounded-lg ${m.projected ? 'bg-[#22C55E]/10 border border-dashed border-[#22C55E]/30' : 'bg-[#0A0A0A]'}`}>
                  <p className="text-xs text-[#999999]">{m.month}</p>
                  <p className={`text-sm font-bold mt-1 ${m.projected ? 'text-[#22C55E]' : 'text-white'}`}>${m.revenue}</p>
                  {m.projected && <p className="text-[10px] text-[#22C55E]/60">projected</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cohorts */}
      {cohorts && cohorts.cohorts?.length > 0 && (
        <Card className="bg-[#111111] border-[#222222]">
          <CardContent className="p-5">
            <h3 className="text-white font-bold mb-4">Cohort Retention</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#999999] text-xs uppercase">
                    <th className="text-left p-2">Period</th>
                    <th className="text-right p-2">Signups</th>
                    <th className="text-right p-2">Retained</th>
                    <th className="text-right p-2">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.cohorts.map((c) => (
                    <tr key={c.period} className="border-t border-[#222222]">
                      <td className="p-2 text-white">{c.period}</td>
                      <td className="p-2 text-right text-white">{c.signups}</td>
                      <td className="p-2 text-right text-white">{c.retained}</td>
                      <td className="p-2 text-right">
                        <span className={`font-bold ${c.rate >= 50 ? 'text-green-400' : c.rate >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>{c.rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function FraudTab() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [filter, setFilter] = useState('open');

  const load = useCallback(async () => {
    try {
      const [a, s, t] = await Promise.all([
        fraudAPI.getAlerts({ status: filter || undefined }),
        fraudAPI.getStats(),
        fraudAPI.getThresholds(),
      ]);
      setAlerts(a.data.alerts || []);
      setStats(s.data);
      setThresholds(t.data);
    } catch (err) { console.error('Failed to load fraud data:', err); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'dismiss') await fraudAPI.dismissAlert(id);
      else await fraudAPI.investigateAlert(id);
      toast.success(`Alert ${action === 'dismiss' ? 'dismissed' : 'marked for investigation'}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const severityBadge = (sev) => {
    const map = { high: 'bg-red-500/10 text-red-400', medium: 'bg-yellow-500/10 text-yellow-400', low: 'bg-blue-500/10 text-blue-400' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${map[sev] || map.low}`}>{sev}</span>;
  };

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Open', value: stats.open, color: 'text-yellow-400' },
            { label: 'High Severity', value: stats.high_severity_open, color: 'text-red-400' },
            { label: 'Investigating', value: stats.investigating, color: 'text-blue-400' },
            { label: 'Dismissed', value: stats.dismissed, color: 'text-[#999999]' },
          ].map((s, i) => (
            <Card key={s.label} className="bg-[#111111] border-[#222222]"><CardContent className="p-3">
              <p className="text-xs text-[#999999]">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {['open', 'investigating', 'dismissed', ''].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${filter === f ? 'bg-[#22C55E] text-black' : 'bg-[#111111] text-[#999999] border border-[#333333]'}`}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-12 text-[#999999]"><ShieldWarning className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No alerts</p></div>
      ) : alerts.map(a => (
        <Card key={a.alert_id} className="bg-[#111111] border-[#222222]" data-testid={`fraud-alert-${a.alert_id}`}>
          <CardContent className="p-4 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {severityBadge(a.severity)}
                <span className="text-xs text-[#999999]">{a.type?.replace(/_/g, ' ')}</span>
              </div>
              <p className="text-white text-sm">{a.message}</p>
              <p className="text-xs text-[#999999] mt-1">{a.user_name} ({a.user_email}) &middot; {new Date(a.created_at).toLocaleString()}</p>
            </div>
            {a.status === 'open' && (
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" onClick={() => handleAction(a.alert_id, 'investigate')} className="bg-blue-600 text-white text-xs">Investigate</Button>
                <Button size="sm" variant="outline" onClick={() => handleAction(a.alert_id, 'dismiss')} className="border-[#333333] text-[#999999] text-xs">Dismiss</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      const { data } = await auditAPI.getLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) { console.error('Failed to load audit logs:', err); }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const actionColors = {
    user_banned: 'text-red-400', user_unbanned: 'text-green-400', role_changed: 'text-blue-400',
    balance_adjusted: 'text-yellow-400', withdrawal_approved: 'text-green-400', withdrawal_rejected: 'text-red-400',
    fee_updated: 'text-purple-400',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#999999]">{total} total actions logged</p>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="bg-[#111111] border border-[#333333] text-white text-xs rounded-lg px-3 py-1.5" data-testid="audit-filter">
          <option value="">All Actions</option>
          <option value="user_banned">User Banned</option>
          <option value="user_unbanned">User Unbanned</option>
          <option value="role_changed">Role Changed</option>
          <option value="balance_adjusted">Balance Adjusted</option>
          <option value="withdrawal_approved">Withdrawal Approved</option>
          <option value="withdrawal_rejected">Withdrawal Rejected</option>
        </select>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-[#999999]"><ClipboardText className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No audit logs</p></div>
      ) : logs.map(log => (
        <Card key={log.audit_id} className="bg-[#111111] border-[#222222]" data-testid={`audit-${log.audit_id}`}>
          <CardContent className="p-3 flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${actionColors[log.action] || 'text-white'}`}>{log.action?.replace(/_/g, ' ')}</span>
                <span className="text-xs text-[#999999]">&middot; {log.target_type} ({log.target_id?.slice(0, 16)})</span>
              </div>
              <p className="text-xs text-[#999999] mt-0.5">by {log.admin_name || log.admin_email} &middot; {new Date(log.created_at).toLocaleString()}</p>
              {log.details && Object.keys(log.details).length > 0 && (
                <p className="text-xs text-[#666666] mt-1">{JSON.stringify(log.details)}</p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function GDPRTab() {
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [processing, setProcessing] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await gdprAPI.exportData();
      // Admin endpoint
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gdpr/admin/deletion-requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('divispend_access_token')}`,
          'X-Access-Token': localStorage.getItem('divispend_access_token'),
        }
      });
      if (res.ok) setDeletionRequests(await res.json());
    } catch (err) { /* silent */ }
  };

  const processDeletion = async (requestId) => {
    if (!window.confirm('This will permanently delete all user data. Are you sure?')) return;
    setProcessing(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/gdpr/admin/process-deletion/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('divispend_access_token')}`,
          'X-Access-Token': localStorage.getItem('divispend_access_token'),
        }
      });
      toast.success('User data deleted');
      load();
    } catch (err) { toast.error('Failed to process deletion'); }
    finally { setProcessing(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-[#111111] border-[#222222]">
        <CardContent className="p-5">
          <h3 className="text-white font-bold mb-2">Data Deletion Requests</h3>
          <p className="text-xs text-[#999999] mb-4">Users can request account deletion. Requests are auto-scheduled for 30 days.</p>

          {deletionRequests.length === 0 ? (
            <p className="text-center text-[#999999] py-6">No deletion requests</p>
          ) : deletionRequests.map(req => (
            <div key={req.request_id} className="bg-[#0A0A0A] rounded-lg p-3 mb-2 flex items-center justify-between" data-testid={`deletion-${req.request_id}`}>
              <div>
                <p className="text-white text-sm font-bold">{req.name} ({req.email})</p>
                <p className="text-xs text-[#999999]">
                  Requested: {new Date(req.requested_at).toLocaleDateString()} &middot;
                  Status: <span className={req.status === 'pending' ? 'text-yellow-400' : req.status === 'completed' ? 'text-green-400' : 'text-[#999999]'}>{req.status}</span>
                </p>
              </div>
              {req.status === 'pending' && (
                <Button size="sm" onClick={() => processDeletion(req.request_id)} disabled={processing} className="bg-red-600 hover:bg-red-700 text-white text-xs">
                  Process Now
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#111111] border-[#222222]">
        <CardContent className="p-5">
          <h3 className="text-white font-bold mb-2">GDPR Compliance Overview</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" weight="fill" /> <span className="text-[#999999]">Data Export — Users can download all their data as JSON</span></div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" weight="fill" /> <span className="text-[#999999]">Right to Deletion — 30-day scheduled deletion with cancellation</span></div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" weight="fill" /> <span className="text-[#999999]">Consent Management — Users control data processing & marketing consent</span></div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" weight="fill" /> <span className="text-[#999999]">Transaction Anonymization — Deleted user transactions are anonymized, not lost</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function AuditChainTab() {
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const pageSize = 25;

  const loadEntries = useCallback(async () => {
    try {
      const res = await auditAPI.getChain({
        action: actionFilter || undefined,
        limit: pageSize,
        skip: page * pageSize,
      });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [actionFilter, page]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const res = await auditAPI.verifyChain(50000);
      setVerifyResult(res.data);
      if (res.data.valid) toast.success('Audit chain verified — no tampering detected');
      else toast.error(`Chain broken at sequence ${res.data.broken_at_seq}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div className="space-y-4" data-testid="audit-chain-tab">
      <Card className="bg-[#111111] border-[#222222]">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <LockKey className="w-5 h-5 text-[#22C55E]" weight="duotone" />
                <h3 className="text-white font-bold">Cryptographic Audit Chain</h3>
              </div>
              <p className="text-xs text-[#999999] mt-1">
                Every privileged action is hash-chained. Verification walks the entire chain and pinpoints any tampering.
              </p>
            </div>
            <Button
              onClick={runVerify}
              disabled={verifying}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
              data-testid="verify-chain-btn"
            >
              {verifying ? 'Verifying…' : 'Verify chain now'}
            </Button>
          </div>
          {verifyResult && (
            <div
              className={`mt-4 rounded-sm border p-3 text-sm flex items-center gap-2 ${
                verifyResult.valid
                  ? 'border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E]'
                  : 'border-red-500/40 bg-red-500/10 text-red-300'
              }`}
              data-testid="verify-chain-result"
            >
              {verifyResult.valid
                ? <><CheckCircle className="w-4 h-4" weight="fill" /> Chain intact through {verifyResult.checked_up_to.toLocaleString()} entries</>
                : <><Warning className="w-4 h-4" weight="fill" /> Tampering detected at seq {verifyResult.broken_at_seq}</>
              }
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#111111] border-[#222222]">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div className="flex-1">
              <Label htmlFor="chain-action-filter" className="text-xs text-[#999999] uppercase tracking-wider">
                Filter by action
              </Label>
              <Input
                id="chain-action-filter"
                placeholder="e.g. auth.login, user.settings.update"
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
                className="bg-[#0A0A0A] border-[#333333] text-white mt-1"
                data-testid="chain-action-filter"
              />
            </div>
            <div className="text-xs text-[#999999]">
              <span className="text-white font-bold">{total.toLocaleString()}</span> total entries
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[#666666] uppercase tracking-wider">
                <tr>
                  <th className="py-2 pr-3">Seq</th>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Actor</th>
                  <th className="py-2 pr-3">Target</th>
                  <th className="py-2 pr-3">Hash</th>
                </tr>
              </thead>
              <tbody className="text-[#BBBBBB]">
                {entries.map((e) => (
                  <tr key={e.seq} className="border-t border-[#1A1A1A]" data-testid={`chain-row-${e.seq}`}>
                    <td className="py-2 pr-3 font-mono text-white">{e.seq}</td>
                    <td className="py-2 pr-3 text-[#999999] whitespace-nowrap">
                      {e.ts ? new Date(e.ts).toLocaleString() : ''}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-mono bg-[#1A1A1A] px-2 py-0.5 rounded-sm">{e.action}</span>
                    </td>
                    <td className="py-2 pr-3 text-[#999999]">
                      {e.actor?.email || e.actor?.type || '—'}
                    </td>
                    <td className="py-2 pr-3 text-[#999999]">
                      {e.target?.type || '—'}{e.target?.id ? ` · ${String(e.target.id).slice(0, 14)}` : ''}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[#22C55E]/70">
                      {e.hash ? `${e.hash.slice(0, 12)}…` : ''}
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-[#666666]">No entries yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-xs text-[#999999]">
            <div>Page {page + 1} / {lastPage + 1}</div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="bg-[#0A0A0A] border-[#333333] text-white"
                data-testid="chain-prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => p + 1)}
                className="bg-[#0A0A0A] border-[#333333] text-white"
                data-testid="chain-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
